<template>
  <div>
    <div v-if="rows && rows.length === 0" class="text-center py-5">
      <i
        class="fe fe-inbox text-muted"
        style="font-size: 48px; opacity: 0.6"
      ></i>
      <p class="mt-3 text-muted">Tidak ada data untuk ditampilkan.</p>
    </div>
    <vue-good-table
      v-else
      :columns="columns"
      :rows="rows"
      :total-rows="total"
      :sort-options="{ enabled: true }"
      :pagination-options="{ enabled: true }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true,
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true,
        selectAllByGroup: true,
      }"
      style-class="vgt-table border-0 table-auto"
      @on-sort-change="$emit('sort-change', $event)"
      @on-page-change="$emit('page-change', $event)"
      @on-per-page-change="$emit('per-page-change', $event)"
      @on-selected-rows-change="$emit('select-change', $event)"
      styleClass="vgt-table condensed"
    >
      <template slot="table-row" slot-scope="props">
        <span
          v-if="props.column.field === 'action'"
          class="d-flex gap-3 justify-content-center align-items-center"
        >
          <b-button
            class="no-wrap btn-pill"
            variant="info"
            size="sm"
            title="View"
            @click="$emit('view-detail', props.row)"
          >
            <span class="fe fe-eye"></span>
          </b-button>
        </span>

        <div
          v-if="props.column.field === 'level'"
          class="d-flex flex-column gap-1 justify-content-start align-items-start"
        >
          <div class="rectangle-container">
            <div
              v-for="n in 5"
              :key="'proficiency_standard-' + n"
              :class="[
                'rectangle',
                {
                  'filled-target':
                    n <= props.row?.competency?.proficiency_standard,
                },
              ]"
            ></div>
          </div>
          <div class="rectangle-container">
            <div
              v-for="n in 5"
              :key="'proficiency_standard-' + n"
              :class="[
                'rectangle',
                {
                  'filled-achievement': n <= props.row?.competency?.proficiency,
                },
              ]"
            ></div>
          </div>
        </div>
        <span
          v-if="props.column.field === 'gap'"
          class="d-flex gap-3 justify-content-start align-items-center"
        >
          <span
            :class="
              props.row?.competency?.difference < 1
                ? 'badge bg-success'
                : 'badge bg-danger'
            "
          >
            {{ props.row?.competency?.difference }}
          </span>
        </span>

        <span v-if="props.column.field === 'status'">
          <div
            class="d-flex flex-row align-items-center justify-content-start"
            @click="$emit('change-status', props.row)"
          >
            <span :class="getTrainingStatusClass(props.row.status)">
              {{ snakeToTitle(props.row.status) }}
            </span>
          </div>
        </span>

        <span v-else class="d-flex align-items-center justify-content-start">
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <template slot="pagination-bottom" slot-scope="props">
        <div
          class="d-flex justify-content-between align-items-center mt-3 px-3"
        >
          <div class="text-muted">
            Showing
            <strong>{{ showingFrom }} - {{ showingTo }}</strong>
            of <strong>{{ total }}</strong> items
          </div>

          <b-pagination
            v-model="page"
            :total-rows="total"
            :per-page="pageSize"
            first-number
            last-number
            align="right"
            size="md"
            @change="$emit('page-change-from-bv', $event)"
          />
        </div>
      </template>
    </vue-good-table>

    <div class="d-flex justify-content-end mt-4">
      <b-button
        class="mr-2"
        variant="light"
        :disabled="isApproveDisabled"
        @click="$emit('reject')"
      >
        Reject ({{ selectedRows.length }})
      </b-button>
      <b-button
        variant="primary"
        :disabled="isApproveDisabled"
        @click="$emit('approve')"
      >
        <template>
          <span class="fe fe-save"></span>
          Approve
        </template>
      </b-button>
    </div>
  </div>
</template>

<script>
import { props } from "vue2-dropzone";
import { getTrainingStatusClass, snakeToTitle } from "../utils/utils";

export default {
  name: "EnrollmentReportTable",
  props: {
    rows: Array,
    total: Number,
    showingFrom: Number,
    showingTo: Number,
    page: Number,
    pageSize: Number,
    selectedRows: {
      type: Array,
      default: () => [],
    },
    isLoadingApprove: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isApproveDisabled() {
      return (
        !this.selectedRows ||
        this.selectedRows.length === 0 ||
        this.isLoadingApprove
      );
    },
  },

  data() {
    return {
      columns: [
        {
          label: "Title",
          field: "training.title",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Competency",
          field: "competency.name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Level",
          field: "level",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Gap",
          field: "gap",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Platform",
          field: "training.platform",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Type",
          field: "training.type.name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "status",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
    };
  },

  methods: {
    getTrainingStatusClass,
    snakeToTitle,
    approve() {
      this.$emit("approve");
    },
  },
};
</script>

<style scoped>
.w-action {
  width: 140px;
  min-width: 140px;
  max-width: 140px;
}

.vgt-table.table-auto table {
  table-layout: auto !important;
}

:deep(.custom-switch .custom-control-label) {
  display: flex !important;
  align-items: center !important;
  cursor: pointer;
}

:deep(
    .custom-switch
      .custom-control-input:not(:checked)
      ~ .custom-control-label::before
  ) {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
}

:deep(
    .custom-switch .custom-control-input:checked ~ .custom-control-label::before
  ) {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

:deep(
    .custom-switch .custom-control-input:checked ~ .custom-control-label::after
  ) {
  background-color: #fff !important;
}

:deep(.table-header) {
  font-size: 12px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 12px !important;
}
</style>
